import { Controller } from 'stimulus'
import Swiper, { Navigation } from 'swiper'

Swiper.use([Navigation])

export default class extends Controller {

  connect() {
    this.slides = this.element.querySelectorAll('.swiper-slide')
    this.indicator = this.element.querySelector('.js-imageCarouselIndicator')
    this.bar = this.element.querySelector('.js-imageCarouselBar')

    this.onDragStart = this.onDragStart.bind(this)
    this.onDragMove = this.onDragMove.bind(this)
    this.onDragEnd = this.onDragEnd.bind(this)
    this.touchStartOptions = { passive: true }
    this.touchMoveOptions = { passive: true }
    this.totalSlides = this.slides.length

    if (this.totalSlides > 1) {
      this.initSlider()
    }
  }

  initSlider() {
    this.swiper = new Swiper(this.element.querySelector('.js-imageCarouselSlider'), {
      slidesPerView: 'auto',
      centeredSlides: true,
      loop: true,
      speed: 800,
      spaceBetween: 24,
      navigation: {
        nextEl: this.element.querySelector('.js-imageCarouselNext'),
        prevEl: this.element.querySelector('.js-imageCarouselPrev'),
      },
      on: {
        slideChange: () => {
          this.updateProgressBar()
        },
        slideChangeTransitionEnd: () => {
          this.updateSlideAccessibility()
        },
        transitionEnd: (swiper) => {
          swiper.slideToLoop(swiper.realIndex, 0, false)
          this.updateSlideAccessibility()
        },
      },
    })

    this.updateProgressBar()
    this.updateSlideAccessibility()
    this.initBarDrag()
  }

  initBarDrag() {
    if (!this.bar || !this.indicator) return

    this.bar.addEventListener('mousedown', this.onDragStart)
    this.bar.addEventListener('touchstart', this.onDragStart, this.touchStartOptions)
  }

  onDragStart(e) {
    if (!this.bar || !this.indicator) return

    this.isDragging = true
    this.dragRatio = undefined
    this.bar.classList.add('is-dragging')
    this.indicator.classList.add('is-dragging')

    document.addEventListener('mousemove', this.onDragMove)
    document.addEventListener('mouseup', this.onDragEnd)
    document.addEventListener('touchmove', this.onDragMove, this.touchMoveOptions)
    document.addEventListener('touchend', this.onDragEnd)

    const clientX = e.touches ? e.touches[0].clientX : e.clientX
    this.dragStartX = clientX
    this.dragStartLeft = parseFloat(this.indicator.style.left) || 0
  }

  onDragMove(e) {
    if (!this.isDragging || !this.bar || !this.indicator) return
    this.moveIndicatorToPointer(e)
  }

  onDragEnd() {
    if (!this.bar || !this.indicator) {
      this.removeDragListeners()
      return
    }

    this.isDragging = false
    this.bar.classList.remove('is-dragging')
    this.indicator.classList.remove('is-dragging')

    this.removeDragListeners()

    if (!this.swiper) return

    const maxIndex = Math.max(this.totalSlides - 1, 1)
    const dragRatio = Number.isFinite(this.dragRatio)
      ? this.dragRatio
      : this.swiper.realIndex / maxIndex
    const slideIndex = Math.round(dragRatio * (this.totalSlides - 1))
    this.swiper.loopFix()
    this.swiper.slideToLoop(slideIndex, 800)
    this.updateProgressBar()
    this.dragRatio = undefined
  }

  moveIndicatorToPointer(e) {
    const clientX = e.touches ? e.touches[0].clientX : e.clientX
    const rect = this.bar.getBoundingClientRect()
    const deltaPercent = ((clientX - this.dragStartX) / rect.width) * 100

    const indicatorWidth = 100 / this.totalSlides
    const maxLeft = 100 - indicatorWidth

    if (maxLeft <= 0) {
      this.indicator.style.left = '0%'
      this.dragRatio = 0
      return
    }

    const newLeft = Math.max(0, Math.min(maxLeft, this.dragStartLeft + deltaPercent))

    this.indicator.style.left = `${newLeft}%`
    this.dragRatio = newLeft / maxLeft
  }

  updateProgressBar() {
    if (!this.indicator || !this.swiper) return

    const width = 100 / this.totalSlides
    const left = (this.swiper.realIndex / this.totalSlides) * 100

    this.indicator.style.width = `${width}%`
    this.indicator.style.left = `${left}%`
  }

  updateSlideAccessibility() {
    if (!this.swiper) return

    const slides = Array.from(this.swiper.slides)

    slides.forEach(slide => {
      const isActive = slide.classList.contains('swiper-slide-active')
      slide.setAttribute('aria-hidden', isActive ? 'false' : 'true')
      slide.inert = !isActive
    })
  }

  removeDragListeners() {
    document.removeEventListener('mousemove', this.onDragMove)
    document.removeEventListener('mouseup', this.onDragEnd)
    document.removeEventListener('touchmove', this.onDragMove, this.touchMoveOptions)
    document.removeEventListener('touchend', this.onDragEnd)
  }

  disconnect() {
    if (this.bar) {
      this.bar.removeEventListener('mousedown', this.onDragStart)
      this.bar.removeEventListener('touchstart', this.onDragStart, this.touchStartOptions)
    }
    this.removeDragListeners()
    if (this.swiper) {
      this.swiper.destroy()
    }
  }
}
